import { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { adminLimit } from '../utils/adminPermissions';
import { Announcement, AnnouncementType, UserRole } from '../types';
import Modal from '../components/Modal';

const TYPE_LABEL: Record<AnnouncementType, string> = {
  [AnnouncementType.NEWS]: 'News',
  [AnnouncementType.DEADLINE]: 'Deadline',
  [AnnouncementType.EVENT]: 'Event',
  [AnnouncementType.SCHOLARSHIP_UPDATE]: 'Scholarship Update',
  [AnnouncementType.MAINTENANCE]: 'Maintenance'
};

function formatDate(value?: string | Date) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const emptyForm = { title: '', content: '', announcementType: AnnouncementType.NEWS, pinned: false, imageUrl: '' };

const AnnouncementsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN;
  // The Super Admin's daily cap for this Admin; 0 means posting is
  // switched off. Reaching a non-zero cap is reported by the server.
  const announcementLimit = adminLimit(user, 'announcementsPerDay');

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = async () => {
    try {
      const result = await apiService.getAnnouncements({ pageSize: 50 });
      setAnnouncements(result.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (announcement: Announcement) => {
    setEditing(announcement);
    setForm({
      title: announcement.title,
      content: announcement.content,
      announcementType: announcement.announcementType ?? AnnouncementType.NEWS,
      pinned: announcement.pinned,
      imageUrl: announcement.imageUrl ?? ''
    });
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await apiService.updateAnnouncement(editing.id, form);
      } else {
        await apiService.createAnnouncement(form);
      }
      setShowForm(false);
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this announcement?')) return;
    setDeletingId(id);
    setError('');
    try {
      await apiService.deleteAnnouncement(id);
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to delete announcement');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <nav className="navbar">
        <div className="navbar-brand">Announcements</div>
        {isAdmin && announcementLimit !== 0 && (
          <div className="navbar-actions">
            <button
              className="btn btn-primary btn-sm"
              title={
                announcementLimit !== null
                  ? `You may post up to ${announcementLimit} announcement${announcementLimit === 1 ? '' : 's'} a day.`
                  : undefined
              }
              onClick={openCreate}
            >
              New Announcement
            </button>
          </div>
        )}
      </nav>

      <div className="container">
        <div className="page-header">
          <h1>Announcements</h1>
          <p>News, deadlines, and updates from the program.</p>
        </div>

        {error && (
          <div
            className="alert-error"
            style={{ padding: '1rem', marginBottom: '1.5rem'
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <p>Loading...</p>
        ) : announcements.length === 0 ? (
          <div className="card">
            <p style={{ color: 'var(--text-secondary)' }}>No announcements yet.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {announcements.map((announcement) => (
              <div key={announcement.id} className="card">
                {/* Wraps so the Edit/Delete pair drops below the text on a
                    narrow screen. Without it the buttons refused to shrink
                    and the text column couldn't either, so the row pushed
                    straight out past the card's right edge. */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}
                >
                  {/* minWidth: 0 lets this actually shrink — a flex item
                      won't go below its content width by default, which is
                      what squeezed the title into one word per line. */}
                  <div style={{ flex: '1 1 16rem', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      {announcement.pinned && <span className="badge badge-warning">Pinned</span>}
                      {announcement.announcementType && (
                        <span className="badge badge-secondary">{TYPE_LABEL[announcement.announcementType]}</span>
                      )}
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{formatDate(announcement.publishedAt)}</span>
                    </div>
                    <h3 style={{ marginBottom: '0.5rem' }}>{announcement.title}</h3>
                    <p style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{announcement.content}</p>
                  </div>
                  {isAdmin && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => openEdit(announcement)}>
                        Edit
                      </button>
                      <button
                        className="btn btn-outline btn-sm"
                        disabled={deletingId === announcement.id}
                        onClick={() => handleDelete(announcement.id)}
                      >
                        {deletingId === announcement.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <Modal title={editing ? 'Edit Announcement' : 'New Announcement'} onClose={() => setShowForm(false)}>
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label htmlFor="annTitle">Title</label>
              <input
                id="annTitle"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="annContent">Content</label>
              <textarea
                id="annContent"
                rows={4}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="annType">Type</label>
              <select
                id="annType"
                value={form.announcementType}
                onChange={(e) => setForm({ ...form, announcementType: e.target.value as AnnouncementType })}
              >
                {Object.values(AnnouncementType).map((type) => (
                  <option key={type} value={type}>
                    {TYPE_LABEL[type]}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={form.pinned}
                  onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
                  style={{ width: 'auto', marginRight: '0.5rem' }}
                />
                Pin to top
              </label>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button className="btn btn-outline" type="button" onClick={() => setShowForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default AnnouncementsPage;
